import axios from 'axios';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS, SearchResults } from '@repo/types';
import API from '../endpoints';
import { useGlobalContext } from '@slc/tools/GlobalContext';

/** Results are cheap to re-fetch and go stale as soon as an article publishes. */
const CACHE_TIME = 60 * 1000;

export const EMPTY_SEARCH_RESULTS: SearchResults = {
  clubs: { items: [], total: 0 },
  players: { items: [], total: 0 },
  news: { items: [], total: 0 },
};

export const useGlobalSearchQuery = (query: string, enabled = true) => {
  const { apiUrl } = useGlobalContext();
  const term = query.trim();

  return useQuery({
    queryKey: ['global-search', term, apiUrl],
    queryFn: () => fetchSearchResults(apiUrl, term),
    enabled: enabled && !!apiUrl && term.length >= SEARCH_MIN_CHARS,
    staleTime: CACHE_TIME,
    // The drawer shows a skeleton on the first search only; from then on the previous results
    // stay put while the next request is in flight. Without this the rails blank and refill on
    // every keystroke, which reads as jitter rather than as progress.
    placeholderData: keepPreviousData,
  });
};

const fetchSearchResults = async (apiUrl: string, q: string): Promise<SearchResults> => {
  const { data } = await axios.get<SearchResults>(`${apiUrl}${API.endpoints.search}`, { params: { q } });
  return data;
};

/**
 * The drawer's own hook: holds the field value, debounces it, and issues the search.
 *
 * `searchTerm` updates on every keystroke so the input stays responsive; `debouncedTerm` is what
 * the query keys on. Below the minimum the query is disabled rather than sent, so the hint line
 * shows instead of a spinner — the design is explicit that 1 character is a hint, not a load.
 */
export const useGlobalSearchLazyQuery = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  const debounceTerm = useDebouncedCallback((value: string) => setDebouncedTerm(value), SEARCH_DEBOUNCE_MS);

  const onSearchTermChange = (value: string) => {
    setSearchTerm(value);
    debounceTerm(value);
  };

  const clear = () => {
    setSearchTerm('');
    // Flushed rather than debounced: a cleared field must stop showing results immediately,
    // not 250ms later.
    debounceTerm.cancel();
    setDebouncedTerm('');
  };

  const { data, isLoading, isFetching, isError } = useGlobalSearchQuery(debouncedTerm);

  const isActive = debouncedTerm.trim().length >= SEARCH_MIN_CHARS;
  const results = isActive ? (data ?? EMPTY_SEARCH_RESULTS) : EMPTY_SEARCH_RESULTS;

  return {
    searchTerm,
    debouncedTerm,
    onSearchTermChange,
    clear,
    results,
    isActive,
    isLoading: isActive && isLoading,
    // `isFetching`, not `isLoading`, is what says the results on screen belong to the term on
    // screen. Under `keepPreviousData` a new term keeps the old data and leaves status 'success',
    // so `isLoading` is false for every search after the first — anything keyed on it observes the
    // previous search's payload alongside the new term.
    isFetching: isActive && isFetching,
    isError,
  };
};
